import { Autocomplete, AutocompleteValue, makeStyles, Popper, PopperProps } from '@freighthub/silhouette-core'
import { useQuery } from '@tanstack/react-query'
import { FC } from 'react'
import { URLPrefix } from 'service/url'
import { useRootStore } from 'service/useRootStore'
import { TTranslate, useI18n } from 'utils/localisation'

export type CommodityAutocompleteProps = {
  selectedValue: string
  onSelect: (commodity: string, commodityID: string) => void
}

type Commodity = {
  _id: string
  translationKey: string
}

export const formatCommodityToAutocompleteValue = (
  { translationKey, _id }: Commodity,
  translate: TTranslate
): AutocompleteValue => ({
  text: translate(translationKey),
  value: _id,
})

export const useCommoditiesQuery = () => {
  const {
    API: { http },
  } = useRootStore()
  const { translate, current } = useI18n()

  return useQuery({
    queryKey: ['commodities', current],
    queryFn: async () => {
      const results = await http.get<Commodity[]>('/commodities', { urlPrefix: URLPrefix.QuotationCore })
      return results.map((commodity) => formatCommodityToAutocompleteValue(commodity, translate))
    },
    initialData: [],
  })
}

const useStyles = makeStyles((theme) => ({
  popover: {
    width: `300px !important`,
    // Higher than that of a modal: https://mui.com/material-ui/customization/z-index/
    zIndex: theme.zIndex.tooltip,
  },
}))

const CommodityListPopper: FC<PopperProps> = (props) => {
  const classes = useStyles()

  return <Popper {...props} className={classes.popover} placement="bottom-end" />
}

export const CommodityAutocomplete: FC<CommodityAutocompleteProps> = ({ selectedValue, onSelect }) => {
  const { translate } = useI18n()
  const { data: commodities } = useCommoditiesQuery()

  return (
    <Autocomplete
      fullWidth
      label={translate('quote_request-cargo-commodity')}
      options={commodities}
      TextFieldProps={{
        required: true,
        helperText: translate('quote_request-cargo-commodity-helper-text'),
        inputProps: {
          'data-e2e-id': 'cargo-commodity-type-input',
        },
        margin: 'none',
      }}
      onChange={(_, selection: AutocompleteValue) => {
        onSelect(selection.text, selection.value as string)
      }}
      value={{ text: selectedValue }}
      getOptionLabel={(option) => option.text}
      PopperComponent={CommodityListPopper}
    />
  )
}
